import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { generateUUID } from "@/lib/utils/uuid";
import { useTranslation } from "react-i18next";

export interface UserInvite {
  id: string;
  email: string;
  role: "admin" | "veterinarian" | "receptionist" | "groomer" | "user";
  clinic_id: string;
  clinic_ids: string[] | null;
  customer_id: string;
  invited_by: string;
  token: string;
  expires_at: string;
  accepted_at: string | null;
  cancelled_at: string | null;
  resent_count: number;
  created_at: string;
}

export interface CreateInviteInput {
  email: string;
  role: "admin" | "veterinarian" | "receptionist" | "groomer" | "user";
  clinic_ids: string[];
  customer_id: string;
}

export function useInvites() {
  return useQuery({
    queryKey: ["user-invites"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_invites")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Converter clinic_ids de Json para string[]
      return (data || []).map((invite) => ({
        ...invite,
        clinic_ids: Array.isArray(invite.clinic_ids)
          ? invite.clinic_ids.filter((id): id is string => typeof id === "string")
          : null,
        cancelled_at: invite.cancelled_at || null,
        resent_count: invite.resent_count || 0,
      })) as UserInvite[];
    },
  });
}

export function useCreateInvite() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("admin");

  return useMutation({
    mutationFn: async (input: CreateInviteInput) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error(t("phase8.invites.errors.notAuthenticated"));

      if (input.clinic_ids.length === 0) {
        throw new Error(t("phase8.invites.errors.selectAtLeastOneClinic"));
      }

      // Check if email already has pending invite
      const { data: existingInvites } = await supabase
        .from("user_invites")
        .select("id")
        .eq("email", input.email)
        .is("accepted_at", null)
        .is("cancelled_at", null);

      if (existingInvites && existingInvites.length > 0) {
        throw new Error(t("phase8.invites.errors.pendingInviteForEmail"));
      }

      // Generate token
      const token = generateUUID();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      // Get inviter profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .single();

      // Buscar nomes das clínicas selecionadas
      const { data: clinicsData, error: clinicsError } = await supabase
        .from("clinics")
        .select("id, name")
        .in("id", input.clinic_ids);

      if (clinicsError) throw new Error(t("phase8.invites.errors.fetchClinics", { message: clinicsError.message }));

      const clinicNames = clinicsData?.map((c) => c.name) || [];
      if (clinicNames.length !== input.clinic_ids.length) {
        throw new Error(t("phase8.invites.errors.clinicsNotFound"));
      }

      // Primeira clínica para compatibilidade com clinic_id
      const firstClinicId = input.clinic_ids[0];

      // Criar apenas um convite com clinic_ids como JSON
      const { data: invite, error: insertError } = await supabase
        .from("user_invites")
        .insert({
          email: input.email,
          role: input.role,
          clinic_id: firstClinicId, // Primeira clínica para compatibilidade
          clinic_ids: input.clinic_ids, // Array completo como JSONB
          customer_id: input.customer_id,
          invited_by: user.id,
          token,
          expires_at: expiresAt.toISOString(),
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Send email with clinic names
      const inviteUrl = `${window.location.origin}/invite?inviteToken=${token}`;
      const { data: emailResponse, error: emailError } = await supabase.functions.invoke("send-invite", {
        body: {
          email: input.email,
          inviteUrl,
          inviterName: profile?.full_name || t("phase8.invites.fallbackInviterName"),
          clinicNames: clinicNames,
        },
      });

      if (emailError) {
        console.error("Erro ao enviar email:", emailError);
        // Verificar se a resposta contém detalhes do erro
        if (emailResponse && !emailResponse.success) {
          console.error("Detalhes do erro de email:", emailResponse);
        }
        // Não lançar erro - o convite foi criado, apenas o email falhou
      }

      return invite;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-invites"] });
      toast({
        title: t("phase8.invites.sentTitle"),
        description: t("phase8.invites.sentDescription"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.invites.sendErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useAcceptInvite() {
  const { t } = useTranslation("admin");
  return useMutation({
    mutationFn: async (token: string) => {
      const { data, error } = await supabase
        .from("user_invites")
        .select("*")
        .eq("token", token)
        .is("accepted_at", null)
        .single();

      if (error) throw new Error(t("phase8.invites.errors.invalidInvite"));

      if (!data) throw new Error(t("phase8.invites.errors.inviteNotFound"));

      // Converter clinic_ids de Json para string[]
      const invite: UserInvite = {
        ...data,
        role: data.role as "admin" | "groomer" | "receptionist" | "user" | "veterinarian",
        clinic_ids: Array.isArray(data.clinic_ids)
          ? data.clinic_ids.filter((id): id is string => typeof id === "string")
          : null,
      };

      if (new Date(invite.expires_at) < new Date()) {
        throw new Error(t("phase8.invites.errors.inviteExpired"));
      }

      return invite;
    },
  });
}

export function useCompleteInvite() {
  const { t } = useTranslation("admin");
  return useMutation({
    mutationFn: async ({ token, userId }: { token: string; userId: string }) => {
      console.log("Iniciando completar convite para token:", token, "userId:", userId);

      // Get invite
      const { data: invite, error: inviteError } = await supabase
        .from("user_invites")
        .select("*")
        .eq("token", token)
        .single();

      if (inviteError) {
        console.error("Erro ao buscar convite:", inviteError);
        throw new Error(t("phase8.invites.errors.fetchInvite", { message: inviteError.message }));
      }

      if (!invite) {
        throw new Error(t("phase8.invites.errors.inviteNotFound"));
      }

      console.log("Convite encontrado:", invite);

      // Processar clinic_ids (usar clinic_ids se disponível, senão usar clinic_id)
      // Converter de Json para string[] se necessário
      const clinicIdsRaw = invite.clinic_ids;
      const clinicIdsParsed: string[] = Array.isArray(clinicIdsRaw) && clinicIdsRaw.length > 0
        ? clinicIdsRaw.filter((id): id is string => typeof id === "string")
        : [];

      const clinicIds: string[] = clinicIdsParsed.length > 0
        ? clinicIdsParsed
        : [invite.clinic_id];

      console.log("Clínicas a processar:", clinicIds);

      // Mark as accepted
      const { error: updateError } = await supabase
        .from("user_invites")
        .update({ accepted_at: new Date().toISOString() })
        .eq("token", token);

      if (updateError) {
        console.error("Erro ao atualizar accepted_at:", updateError);
        throw new Error(t("phase8.invites.errors.markAccepted", { message: updateError.message }));
      }

      console.log("Convite marcado como aceito");

      const roleValue = invite.role === "veterinarian" ? "veterinary" : invite.role;
      const { data: role, error: lookupError } = await supabase.from("customer_roles").select("id").eq("value", roleValue).single();
      if (lookupError) throw new Error(t("phase8.invites.errors.addRole", { message: lookupError.message }));
      const { error: roleError } = await supabase.from("user_customer_access").upsert({
        user_id: userId,
        customer_id: invite.customer_id,
        customer_role_id: role.id,
      }, { onConflict: "user_id,customer_id", ignoreDuplicates: true });
      if (roleError) throw new Error(t("phase8.invites.errors.addRole", { message: roleError.message }));

      // Add clinic access for all clinics
      const accessPromises = clinicIds.map((clinicId) =>
        supabase
          .from("user_clinic_access")
          .insert({
            user_id: userId,
            clinic_id: clinicId,
          })
      );

      const accessResults = await Promise.all(accessPromises);
      const accessErrors = accessResults.filter((result) => result.error);

      if (accessErrors.length > 0) {
        console.error("Erros ao adicionar acesso às clínicas:", accessErrors);
        // Filtrar erros que não são duplicatas (erros esperados se já existe acesso)
        const criticalErrors = accessErrors.filter((err) => {
          const errorMsg = err.error?.message || "";
          const errorCode = err.error?.code || "";
          return !errorMsg.includes("duplicate") && !errorCode.includes("23505");
        });

        if (criticalErrors.length > 0) {
          // Verificar se todos falharam ou apenas alguns
          const allFailed = criticalErrors.length === clinicIds.length;
          if (allFailed) {
            const errorMsg = criticalErrors[0].error?.message || t("phase8.invites.errors.unknownError");
            throw new Error(t("phase8.invites.errors.addClinicAccess", { message: errorMsg }));
          }
          // Se apenas alguns falharam, logar mas continuar
          console.warn("Algumas clínicas não puderam ser adicionadas, mas continuando...");
        } else {
          console.log("Acesso às clínicas já existia ou foi adicionado com sucesso");
        }
      } else {
        console.log("Acesso às clínicas adicionado com sucesso");
      }

      return invite;
    },
  });
}

export function useResendInvite() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("admin");

  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { data: invite, error: fetchError } = await supabase
        .from("user_invites")
        .select("*")
        .eq("id", inviteId)
        .single();

      if (fetchError) throw fetchError;

      // Update resent_count
      const { error: updateError } = await supabase
        .from("user_invites")
        .update({ resent_count: (invite.resent_count || 0) + 1 })
        .eq("id", inviteId);

      if (updateError) throw updateError;

      // Get inviter profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error(t("phase8.invites.errors.notAuthenticated"));

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .single();

      // Get clinic names
      const clinicIdsRaw = invite.clinic_ids;
      const clinicIds: string[] = Array.isArray(clinicIdsRaw) && clinicIdsRaw.length > 0
        ? clinicIdsRaw.filter((id): id is string => typeof id === "string")
        : [invite.clinic_id];
      
      const { data: clinicsData } = await supabase
        .from("clinics")
        .select("name")
        .in("id", clinicIds);

      const clinicNames = clinicsData?.map((c) => c.name) || [];

      // Resend email
      const inviteUrl = `${window.location.origin}/invite?inviteToken=${invite.token}`;
      await supabase.functions.invoke("send-invite", {
        body: {
          email: invite.email,
          inviteUrl,
          inviterName: profile?.full_name || t("phase8.invites.fallbackInviterName"),
          clinicNames,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-invites"] });
      toast({
        title: t("phase8.invites.resentTitle"),
        description: t("phase8.invites.resentDescription"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.invites.resendErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useCancelInvite() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("admin");

  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { error } = await supabase
        .from("user_invites")
        .update({ cancelled_at: new Date().toISOString() })
        .eq("id", inviteId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-invites"] });
      toast({
        title: t("phase8.invites.cancelledTitle"),
        description: t("phase8.invites.cancelledDescription"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.invites.cancelErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
